/**
 * RemediationWorkflowModal - TASK-256
 * Workflow engine for AML alert remediation
 * Ported from prototypes/aml-sentinel/components/AlertsManager.tsx
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  CheckSquare,
  ListChecks,
  History,
  Share2,
  Briefcase,
  Loader2,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { WORKFLOW_TEMPLATES } from '../../constants/workflowTemplates';
import {
  listWorkflowTemplates,
  startWorkflow,
  getWorkflowByAlert,
  completeWorkflowStep,
  getAuditTrail,
  addAuditEntry,
  WorkflowTemplate,
  WorkflowInstance,
  AuditEntry,
} from '../../services/amlService';

interface RemediationWorkflowModalProps {
  isOpen: boolean;
  alertId: string;
  alertDescription: string;
  onClose: () => void;
  onWorkflowComplete?: () => void;
}

export const RemediationWorkflowModal: React.FC<RemediationWorkflowModalProps> = ({
  isOpen,
  alertId,
  alertDescription,
  onClose,
  onWorkflowComplete,
}) => {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>(
    WORKFLOW_TEMPLATES as unknown as WorkflowTemplate[]
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [activeWorkflow, setActiveWorkflow] = useState<WorkflowInstance | null>(null);
  const [auditTrail, setAuditTrail] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [startingWorkflow, setStartingWorkflow] = useState(false);
  const [completingStep, setCompletingStep] = useState<string | null>(null);

  // Load existing workflow and audit trail on mount
  useEffect(() => {
    if (isOpen && alertId) {
      loadWorkflowData();
    }
  }, [isOpen, alertId]);

  const loadWorkflowData = async () => {
    setLoading(true);
    try {
      // Check if there's an existing workflow for this alert
      const workflowResponse = await getWorkflowByAlert(alertId);
      if (workflowResponse.has_workflow && workflowResponse.workflow) {
        setActiveWorkflow(workflowResponse.workflow);
      }

      // Load audit trail
      const auditResponse = await getAuditTrail(alertId);
      setAuditTrail(auditResponse.entries);

      // Try to load templates from backend
      try {
        const templatesResponse = await listWorkflowTemplates();
        if (templatesResponse.templates.length > 0) {
          setTemplates(templatesResponse.templates);
        }
      } catch {
        // Use local templates as fallback
        console.log('Using local workflow templates');
      }
    } catch (e) {
      console.error('Failed to load workflow data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleStartWorkflow = async () => {
    if (!selectedTemplateId) return;

    setStartingWorkflow(true);
    try {
      const result = await startWorkflow(alertId, selectedTemplateId);
      if (result.success) {
        setActiveWorkflow(result.workflow);
        // Add to local audit trail
        const newEntry: AuditEntry = {
          id: `local-${Date.now()}`,
          alertId,
          action: 'Workflow Started',
          details: `Started workflow: ${result.workflow.templateName}`,
          performedBy: 'Current User',
          timestamp: new Date().toISOString(),
        };
        setAuditTrail((prev) => [newEntry, ...prev]);
      }
    } catch (e) {
      console.error('Failed to start workflow:', e);
      // Fallback: Create local workflow from template
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (template) {
        const localWorkflow: WorkflowInstance = {
          id: `local-wf-${Date.now()}`,
          templateId: template.id,
          templateName: template.name,
          alertId,
          startedAt: new Date().toISOString(),
          startedBy: 'Current User',
          status: 'in_progress',
          currentStepIndex: 0,
          steps: template.steps.map((s) => ({ ...s, status: 'pending' as const })),
        };
        setActiveWorkflow(localWorkflow);
      }
    } finally {
      setStartingWorkflow(false);
    }
  };

  const handleCompleteStep = async (stepId: string) => {
    if (!activeWorkflow) return;

    setCompletingStep(stepId);
    try {
      const result = await completeWorkflowStep(activeWorkflow.id, stepId);
      if (result.success) {
        setActiveWorkflow(result.workflow);
        // Add to local audit trail
        const step = activeWorkflow.steps.find((s) => s.id === stepId);
        const newEntry: AuditEntry = {
          id: `local-${Date.now()}`,
          alertId,
          action: 'Step Completed',
          details: step?.title || 'Unknown step',
          performedBy: 'Current User',
          timestamp: new Date().toISOString(),
          workflowStepId: stepId,
        };
        setAuditTrail((prev) => [newEntry, ...prev]);

        // Check if workflow is complete
        if (result.workflow.status === 'completed') {
          onWorkflowComplete?.();
        }
      }
    } catch (e) {
      console.error('Failed to complete step:', e);
      // Fallback: Update local workflow
      setActiveWorkflow((prev) => {
        if (!prev) return null;
        const updatedSteps = prev.steps.map((s) =>
          s.id === stepId
            ? {
                ...s,
                status: 'completed' as const,
                completedAt: new Date().toISOString(),
                completedBy: 'Current User',
              }
            : s
        );
        const allComplete = updatedSteps.every((s) => s.status === 'completed');
        return {
          ...prev,
          steps: updatedSteps,
          status: allComplete ? 'completed' : 'in_progress',
          currentStepIndex: updatedSteps.findIndex((s) => s.status !== 'completed'),
        };
      });
    } finally {
      setCompletingStep(null);
    }
  };

  const handleIntegrationAction = async (actionName: string) => {
    // Add audit entry
    const newEntry: AuditEntry = {
      id: `local-${Date.now()}`,
      alertId,
      action: 'Integration Triggered',
      details: actionName,
      performedBy: 'Current User',
      timestamp: new Date().toISOString(),
    };
    setAuditTrail((prev) => [newEntry, ...prev]);

    try {
      await addAuditEntry(alertId, 'Integration Triggered', actionName);
    } catch (e) {
      console.error('Failed to log integration action:', e);
    }
  };

  // Calculate progress
  const progress = activeWorkflow
    ? Math.round(
        (activeWorkflow.steps.filter((s) => s.status === 'completed').length /
          activeWorkflow.steps.length) *
          100
      )
    : 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Remediation Workflow</h2>
            <p className="text-sm text-gray-500 mt-1">{alertDescription}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin text-gray-400" size={32} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              {/* Left: Workflow */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ListChecks size={18} className="text-blue-600" />
                    <h3 className="font-bold text-gray-800">Workflow Steps</h3>
                  </div>
                  {activeWorkflow && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                      {progress}% Complete
                    </span>
                  )}
                </div>

                <div className="p-4">
                  {/* Workflow Selector */}
                  {!activeWorkflow && (
                    <div className="mb-6">
                      <label className="block text-xs font-medium text-gray-500 mb-2">
                        Select Workflow Template
                      </label>
                      <select
                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 p-2 border"
                        value={selectedTemplateId}
                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                      >
                        <option value="">Choose a template...</option>
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name} ({t.forRiskLevel})
                          </option>
                        ))}
                      </select>
                      {selectedTemplateId && (
                        <button
                          onClick={handleStartWorkflow}
                          disabled={startingWorkflow}
                          className="mt-3 w-full bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
                        >
                          {startingWorkflow ? (
                            <Loader2 className="animate-spin" size={16} />
                          ) : (
                            <CheckSquare size={16} />
                          )}
                          {startingWorkflow ? 'Starting...' : 'Start Workflow'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Active Workflow Steps */}
                  {activeWorkflow ? (
                    <div className="space-y-3">
                      {/* Progress Bar */}
                      <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden mb-4">
                        <div
                          className="bg-blue-600 h-full transition-all duration-500"
                          style={{ width: `${progress}%` }}
                        />
                      </div>

                      <h4 className="text-sm font-bold text-gray-800 mb-2">
                        {activeWorkflow.templateName}
                      </h4>

                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {activeWorkflow.steps.map((step) => (
                          <div
                            key={step.id}
                            onClick={() =>
                              step.status !== 'completed' && handleCompleteStep(step.id)
                            }
                            className={`p-3 rounded-lg border flex items-start gap-3 transition-colors ${
                              step.status === 'completed'
                                ? 'bg-green-50 border-green-200'
                                : 'bg-white border-gray-200 hover:border-blue-300 cursor-pointer'
                            } ${completingStep === step.id ? 'opacity-50' : ''}`}
                          >
                            <div
                              className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                step.status === 'completed'
                                  ? 'bg-green-500 border-green-500'
                                  : 'bg-white border-gray-300'
                              }`}
                            >
                              {step.status === 'completed' && (
                                <CheckCircle size={14} className="text-white" />
                              )}
                              {completingStep === step.id && (
                                <Loader2 size={12} className="animate-spin text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1">
                              <span
                                className={`text-sm ${step.status === 'completed' ? 'text-green-800 line-through decoration-green-800/30' : 'text-gray-700'}`}
                              >
                                {step.title}
                              </span>
                              <p className="text-xs text-gray-500 mt-1">{step.description}</p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                                <span className="flex items-center gap-1">
                                  <Clock size={10} />
                                  {step.estimatedDuration}
                                </span>
                                <span>{step.assignedRole}</span>
                              </div>
                              {step.completedAt && (
                                <p className="text-xs text-green-600 mt-1">
                                  Completed: {new Date(step.completedAt).toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Workflow Complete Message */}
                      {activeWorkflow.status === 'completed' && (
                        <div className="bg-green-50 border border-green-200 p-4 rounded-lg text-center">
                          <CheckCircle className="text-green-600 mx-auto mb-2" size={32} />
                          <p className="text-green-800 font-semibold">Workflow Complete!</p>
                          <p className="text-green-600 text-sm mt-1">
                            All steps have been completed successfully.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-48 text-gray-400 border-2 border-dashed border-gray-100 rounded-xl">
                      <CheckSquare size={32} className="mb-2 opacity-20" />
                      <p className="text-sm">Select a template to start a workflow.</p>
                    </div>
                  )}
                </div>

                {/* Integration Actions */}
                {activeWorkflow && (
                  <div className="p-4 bg-gray-50 border-t border-gray-200 grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleIntegrationAction('File SAR with FINCEN')}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <Share2 size={14} /> File SAR
                    </button>
                    <button
                      onClick={() => handleIntegrationAction('Sync to Case Manager')}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <Briefcase size={14} /> Sync to CRM
                    </button>
                  </div>
                )}
              </div>

              {/* Right: Audit Trail */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                  <History size={18} className="text-gray-600" />
                  <h3 className="font-bold text-gray-800">Audit Trail</h3>
                </div>
                <div className="max-h-[450px] overflow-y-auto p-4">
                  {auditTrail.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">
                      No activity recorded yet.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {auditTrail.map((log, i) => (
                        <div key={log.id || i} className="flex gap-3 relative">
                          <div className="flex flex-col items-center">
                            <div className="w-2 h-2 bg-gray-300 rounded-full z-10" />
                            {i < auditTrail.length - 1 && (
                              <div className="w-px h-full bg-gray-200 absolute top-2" />
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-700">{log.action}</p>
                            {log.details && (
                              <p className="text-xs text-gray-500 mt-0.5">{log.details}</p>
                            )}
                            <p className="text-[10px] text-gray-400 mt-1">
                              {new Date(log.timestamp).toLocaleString()} • {log.performedBy}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          {activeWorkflow?.status === 'completed' && (
            <button
              onClick={() => {
                onWorkflowComplete?.();
                onClose();
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <CheckCircle size={16} />
              Mark Investigation Complete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RemediationWorkflowModal;
